import moment from 'moment';

import type { CredentialCardProps } from '@Components';
import type { ConnectedChainCardProps } from '@Components/ConnectedChainCard';

const buildCardProps = (
  item: CredentialCardProps,
  expiringTypes: Set<string>,
  onOpenForm: () => void,
  t: (key: string) => string,
): ConnectedChainCardProps => {
  const { credential, icon, name, platform = '', instructions, avatarInitials, avatarColor, readOnly, fields } = item;
  const avatar = icon
    ? { image: icon, alt: name }
    : { initials: avatarInitials ?? name.slice(0, 2), color: avatarColor };

  const hasCredential = !!credential.createdAt;
  const isExpiring = expiringTypes.has(credential.connectorType);
  const isExpired = credential.status === 'EXPIRED';
  const userLabel = credential.accountEmail || undefined;
  const userLabelKey = fields?.find((f) => f.storageKey === 'accountEmail')?.labelKey;
  const lastSyncLabel = credential.credentialUpdatedAt ? moment(credential.credentialUpdatedAt).fromNow() : '—';

  if (!hasCredential) {
    return {
      status: 'available',
      name,
      platform,
      avatar,
      lastSyncLabel: '—',
      instructions,
      readOnly,
      onConnect: readOnly ? undefined : onOpenForm,
    };
  }

  if (isExpired) {
    return {
      status: 'expired',
      name,
      platform,
      avatar,
      lastSyncLabel,
      userLabel,
      userLabelKey,
      instructions,
      readOnly,
      warningMessage: t('Credentials.Card.WarningExpired'),
      onUpdateCredentials: onOpenForm,
      onEdit: onOpenForm,
    };
  }

  if (isExpiring) {
    return {
      status: 'expiring',
      name,
      platform,
      avatar,
      lastSyncLabel,
      userLabel,
      userLabelKey,
      instructions,
      readOnly,
      warningMessage: t('Credentials.Card.WarningExpiring'),
      onUpdateCredentials: onOpenForm,
      onEdit: onOpenForm,
    };
  }

  return {
    status: 'connected',
    name,
    platform,
    avatar,
    lastSyncLabel,
    userLabel,
    userLabelKey,
    instructions,
    readOnly,
    onEdit: onOpenForm,
  };
};

export default buildCardProps;
